/**
 * Spec 027: how an agent's card reads to a person — the parts, the approval, the limits.
 *
 * Pure functions, so the words are the same on the list, the card and in tests.
 */

import type {
  AgentAction,
  AgentActiveRun,
  AgentHelperOut,
  AgentLimits,
  AgentOut,
  AgentRequestKind,
  AgentResultMode,
  AgentRunState,
  AgentSchemeStep,
  AgentStepStatus,
  AgentStopReason,
  HelperMode,
  PermissionAsked,
} from "@/api/types";
import { pluralRu } from "@/lib/format";

/** The scheme's word for the staff member itself (a helper's card: the helper itself). */
export const ORCHESTRATOR = "orchestrator";

/** The parts of the card as the history and the diff name them. */
export const PART_LABEL: Record<string, string> = {
  setup_md: "Установка",
  capabilities: "Возможности",
  scheme: "Схема процесса",
  subagents: "Помощники",
  skills: "Навыки",
  skills_ticked: "Отмеченные навыки",
  model: "Модель Claude Code",
  codex_model: "Модель Codex",
  helper_mode: "Режим помощника",
  chats: "Чаты",
  result_mode: "Режим результата",
  confirmations: "Подтверждения",
  rights: "Права",
  limits: "Лимиты",
};

/** Spec 042: where a helper works. */
export const HELPER_MODE_LABEL: Record<HelperMode, string> = {
  background: "в чате сотрудника",
  own_chat: "в своём чате",
};

export const ACTION_LABEL: Record<AgentAction, string> = {
  read: "Читать",
  discuss: "Писать в обсуждениях",
  tasks: "Задачи и журнал",
  drafts: "Черновики кейсов и заметок",
  files: "Файлы",
};

export const ACTIONS = Object.keys(ACTION_LABEL) as AgentAction[];

export const MODE_LABEL: Record<AgentResultMode, string> = {
  wait: "Ждать «да» перед следующей итерацией",
  parallel: "Отдать на проверку и идти дальше",
};

export function partLabel(part: string): string {
  return PART_LABEL[part] ?? part;
}

/** «сотрудник» (the card's own chat) or the helper's name. */
export function whoLabel(who: string): string {
  return who.trim().toLowerCase() === ORCHESTRATOR ? "сотрудник" : who;
}

export type ApprovalTone = "done" | "needs" | "waiting";

/** Where my computer stands with this agent: approved, or what waits for my «да». */
export function approvalLine(a: Pick<AgentOut, "version" | "my_approved_version" | "archived_at">): {
  tone: ApprovalTone;
  text: string;
} {
  if (a.archived_at) return { tone: "waiting", text: "в архиве" };
  if (a.my_approved_version === a.version) return { tone: "done", text: `одобрена тобой: версия ${a.version}` };
  if (a.my_approved_version == null) return { tone: "needs", text: `версия ${a.version} не одобрена тобой` };
  return { tone: "needs", text: `версия ${a.version} ждёт твоего одобрения (одобрена ${a.my_approved_version})` };
}

/** Spec 042: «в своём чате · одобрена тобой: версия 2 · работает» — a helper on its staff member's card. */
export function helperLine(h: Pick<AgentHelperOut, "helper_mode" | "version" | "my_approved_version" | "archived_at" | "running">): string {
  const parts = [HELPER_MODE_LABEL[h.helper_mode], approvalLine(h).text];
  if (h.running) parts.push("работает");
  return parts.join(" · ");
}

/** Spec 042: what my «Одобрить» on a staff member still owes — its version, its helpers' current
 * versions (one button covers them), or nothing. */
export function approvalNeed(
  a: Pick<AgentOut, "version" | "my_approved_version" | "archived_at" | "helpers_approved">,
): "version" | "helpers" | null {
  if (a.archived_at) return null;
  if (a.my_approved_version !== a.version) return "version";
  return a.helpers_approved === false ? "helpers" : null;
}

const TOKENS = (n: number) =>
  n >= 1_000_000 ? `${(n / 1_000_000).toLocaleString("ru-RU", { maximumFractionDigits: 1 })} млн`
    : n >= 1_000 ? `${Math.round(n / 1_000)} тыс.` : String(n);

/** «20 итераций за запуск · 1 млн токенов в сутки · Higgsfield: 50 кредит в сутки». */
export function limitsLine(limits: AgentLimits): string {
  const parts: string[] = [];
  if (limits.iterations_per_run) {
    const n = limits.iterations_per_run;
    parts.push(`${n} ${pluralRu(n, "итерация", "итерации", "итераций")} за запуск`);
  }
  if (limits.tokens_per_run) parts.push(`${TOKENS(limits.tokens_per_run)} токенов за запуск`);
  if (limits.tokens_per_day) parts.push(`${TOKENS(limits.tokens_per_day)} токенов в сутки`);
  for (const s of limits.services) parts.push(`${s.name}: ${s.per_day} ${s.unit} в сутки`);
  return parts.length ? parts.join(" · ") : "без лимитов";
}

export type Verdict = "safe" | "accepted" | "caution" | "blocked";

/** A skill's SkillSpector verdict from its registry tags; blocked wins, as on the server. */
export function verdictOf(tags: string[] | null | undefined): Verdict | null {
  const marks = new Set((tags ?? []).filter((t) => t.startsWith("skillspector:")).map((t) => t.slice(13)));
  for (const mark of ["blocked", "safe", "accepted", "caution"] as const) if (marks.has(mark)) return mark;
  return null;
}

export const VERDICT_LABEL: Record<Verdict, string> = {
  safe: "проверен",
  accepted: "принят после разбора",
  caution: "осторожно — только отметкой",
  blocked: "заблокирован",
};

/** An edited line split around its changed span, so the span reads in bold. */
export function markSplit(text: string, mark: [number, number] | null | undefined): [string, string, string] {
  if (!mark) return [text, "", ""];
  // The server counts characters (code points); `slice` counts UTF-16 units, and an emoji is
  // two of them (review of PR #47).
  const chars = Array.from(text);
  const [start, end] = mark;
  return [chars.slice(0, start).join(""), chars.slice(start, end).join(""), chars.slice(end).join("")];
}

/** «Одобрить» only once the person can see what they approve (review of PR #46). */
export function approvable(diff: { isSuccess: boolean; data?: unknown }): boolean {
  return diff.isSuccess && diff.data != null;
}

/** A number field's text as the limit it sets: empty is «no limit». */
export function limitOf(text: string): number | null {
  const n = Number(text.trim().replace(/\s/g, ""));
  return text.trim() && Number.isFinite(n) && n >= 1 ? Math.round(n) : null;
}

/* ── Spec 027, PR 2: runs ─────────────────────────────────────────────── */

export const RUN_STATE: Record<AgentRunState, string> = {
  active: "идёт",
  paused: "на паузе",
  stopped: "остановлен",
};

export const STOP_REASON: Record<AgentStopReason, string> = {
  done: "работа сделана",
  person: "остановил человек",
  takeover: "перехвачен в другом чате",
  limit: "лимит не продлили",
};

export const REQUEST_KIND: Record<AgentRequestKind, string> = {
  question: "Вопрос агента",
  right: "Агент просит право",
  limit: "Лимит исчерпан",
  second_window: "Похоже, второе окно",
  card_change: "Агент предлагает поправить себя",
  permission: "Агент просит разрешить инструмент",
};

/** «только что», «5 минут назад», «3 часа назад», «2 дня назад» — never a guess about the future. */
export function sinceWords(iso: string | null | undefined, now: number = Date.now()): string {
  if (!iso) return "нет данных";
  const minutes = Math.max(0, Math.floor((now - new Date(iso).getTime()) / 60_000));
  if (minutes < 1) return "только что";
  if (minutes < 60) return `${minutes} ${pluralRu(minutes, "минуту", "минуты", "минут")} назад`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} ${pluralRu(hours, "час", "часа", "часов")} назад`;
  const days = Math.floor(hours / 24);
  return `${days} ${pluralRu(days, "день", "дня", "дней")} назад`;
}

/** A chat id as a person can match it with their terminal: the first characters. */
export function shortChat(chatId: string): string {
  return chatId.length > 12 ? `${chatId.slice(0, 8)}…` : chatId;
}

/** «Идёт на «Агенты · Claude Code…», чат ab12cd34… · запустил Костя»; a stopped one «Шёл на…». */
export function runWhere(run: Pick<AgentActiveRun, "state" | "computer" | "chat_id" | "launched_by">): string {
  const state = run.state === "stopped" ? "шёл" : RUN_STATE[run.state];
  const where = run.computer ? ` на «${run.computer}»` : "";
  return `${state[0].toUpperCase()}${state.slice(1)}${where}, чат ${shortChat(run.chat_id)} · запустил ${run.launched_by.name}`;
}

/** «Последнее действие: Записать ход выполнения, 5 минут назад» — the takeover offer's clock (D8). */
export function lastAction(run: Pick<AgentActiveRun, "last_action" | "last_action_at">, now: number = Date.now()): string {
  if (!run.last_action_at) return "Действий ещё не было";
  return `Последнее действие${run.last_action ? `: ${run.last_action}` : ""}, ${sinceWords(run.last_action_at, now)}`;
}

/* ── Spec 027, PR 3: work ─────────────────────────────────────────────── */

export const STEP_STATUS: Record<AgentStepStatus, string> = {
  waiting: "ждёт",
  claimed: "в работе",
  done: "готов",
  returned: "возвращён",
};

/** «12 минут», «1,5 часа», «нет данных» — the statistics never guess (D12). */
export function minutesWords(minutes: number | null | undefined): string {
  if (minutes == null) return "нет данных";
  if (minutes < 60) {
    const n = Math.round(minutes);
    return `${n} ${pluralRu(n, "минута", "минуты", "минут")}`;
  }
  const hours = Math.round((minutes / 60) * 10) / 10;
  return `${hours.toLocaleString("ru-RU")} ч`;
}

/** The buttons a person decides a run's request with, by its kind (D7-D9). A limit not
 * extended stops the run, and the button says so. */
export function decisionLabels(kind: AgentRequestKind): { grant: string; refuse: string } {
  if (kind === "limit") return { grant: "Продлить", refuse: "Остановить запуск" };
  if (kind === "second_window") return { grant: "Окно одно, продолжить", refuse: "Остановить запуск" };
  if (kind === "right") return { grant: "Разрешить в этом запуске", refuse: "Отказать" };
  if (kind === "card_change") return { grant: "Принять", refuse: "Не надо" };
  if (kind === "permission") return { grant: "Разрешить один раз", refuse: "Отказать" };
  return { grant: "Разрешить", refuse: "Отказать" };
}

/** What a person hears once decided: for a limit or a second window, what the run does.
 * Spec 041: `always` — a permission granted with a rule for the run. */
export function decidedText(kind: AgentRequestKind, decision: "answer" | "grant" | "refuse", always = false): string {
  if (decision === "answer") return "Ответ передан агенту";
  if (kind === "card_change") return decision === "grant" ? "Правка принята — новая версия одобрена для тебя" : "Правка отклонена";
  if (kind === "limit" || kind === "second_window") {
    if (decision === "refuse") return "Запуск остановлен";
    return kind === "limit" ? "Лимит продлён" : "Запуск продолжается";
  }
  if (kind === "permission") {
    if (decision !== "grant") return "Отказано: агент увидит отказ";
    return always ? "Разрешено — и такие команды в этом запуске больше не спросят" : "Разрешение отправлено для этого запроса";
  }
  return decision === "grant" ? "Разрешено для этого запуска" : "Отказано";
}

/** Spec 041: what «Разрешить и не спрашивать» would allow — as the server offers it with the
 * question, or nothing when no rule is made of it (a variable, a sub-expression, not a shell). */
export function ruleOffer(p: PermissionAsked | null | undefined): string | null {
  const offer = (p as (PermissionAsked & { offer?: string | null }) | null | undefined)?.offer;
  return offer ? offer : null;
}

/** The third button's words: the rule exactly as it will be made. */
export function ruleLabel(offer: string): string {
  return `Разрешить и не спрашивать: ${offer}`;
}

/** Spec 031 A1 §5.3: what a person reads under a permission request — how long it waits, that the
 * terminal asks the same, and how much of the input is shown. `open` false: nothing to answer. */
export function permissionNote(p: Pick<PermissionAsked, "input" | "input_chars" | "expires_at">, now: number = Date.now()): {
  open: boolean;
  wait: string;
  cut: string | null;
} {
  const left = Math.floor((Date.parse(p.expires_at) - now) / 60_000);
  const cut = p.input_chars > p.input.length
    ? `Показано ${p.input.length.toLocaleString("ru-RU")} из ${p.input_chars.toLocaleString("ru-RU")} знаков: остальное — в терминале на ПК.`
    : null;
  if (Date.parse(p.expires_at) <= now) {
    return { open: false, wait: "Срок вышел: вопрос остался в терминале на ПК.", cut };
  }
  // Spec 040: a question lives while its staff member waits, up to a day: hours, not 1 400 minutes.
  const hours = Math.floor(left / 60);
  const wait = left < 1
    ? "меньше минуты"
    : left < 120 ? `${left} ${pluralRu(left, "минуту", "минуты", "минут")}` : `${hours} ${pluralRu(hours, "час", "часа", "часов")}`;
  return {
    open: true,
    wait: `Ждёт ещё ${wait}. Тот же вопрос — в терминале на ПК: кто ответит первым, тот и решил. «Разрешить один раз» — только на этот вызов.`,
    cut,
  };
}

/** D3: the scheme in layers — a step sits one layer below the last step it waits for. */
export function schemeLayers(scheme: AgentSchemeStep[]): AgentSchemeStep[][] {
  const byKey = new Map(scheme.map((s) => [s.key, s]));
  const depth = new Map<string, number>();
  const visiting = new Set<string>();
  const level = (key: string): number => {
    const known = depth.get(key);
    if (known !== undefined) return known;
    if (visiting.has(key)) return 0; // a loop the server refuses: drawn flat, never endless
    visiting.add(key);
    const before = (byKey.get(key)?.after ?? []).filter((k) => k !== key && byKey.has(k));
    const d = before.length ? 1 + Math.max(...before.map(level)) : 0;
    visiting.delete(key);
    depth.set(key, d);
    return d;
  };
  const layers: AgentSchemeStep[][] = [];
  for (const step of scheme) (layers[level(step.key)] ??= []).push(step);
  return layers.filter(Boolean);
}

/** What the steps wait for, as arrows: [from, to] keys of the scheme's own steps. */
export function schemeEdges(scheme: AgentSchemeStep[]): [string, string][] {
  const keys = new Set(scheme.map((s) => s.key));
  return scheme.flatMap((s) => s.after.filter((k) => k !== s.key && keys.has(k)).map((k): [string, string] => [k, s.key]));
}

/** The steps that wait for this one, directly or not: none of them may be what it waits for. */
export function dependentsOf(scheme: AgentSchemeStep[], key: string): Set<string> {
  const out = new Set<string>();
  for (let grew = true; grew; ) {
    grew = false;
    for (const s of scheme) {
      if (!out.has(s.key) && s.key !== key && s.after.some((k) => k === key || out.has(k))) {
        out.add(s.key);
        grew = true;
      }
    }
  }
  return out;
}

const LATIN: Record<string, string> = Object.fromEntries(
  [..."абвгдеёжзийклмнопрстуфхцчшщъыьэюя"].map((ch, i) =>
    [ch, "a b v g d e e zh z i y k l m n o p r s t u f h ts ch sh sch _ y _ e yu ya".split(" ")[i].replace("_", "")]),
);

/** A new step's key from its title: latin and unique — «Собрать спрос» → «sobrat-spros». */
export function stepKey(title: string, taken: Iterable<string>): string {
  const used = new Set(taken);
  const latin = [...title.toLowerCase()].map((ch) => LATIN[ch] ?? ch).join("");
  const base = latin.replace(/[^a-z0-9]+/g, "-").replace(/^-+/, "").slice(0, 36).replace(/-+$/, "") || "step";
  let key = base;
  for (let n = 2; used.has(key); n += 1) key = `${base}-${n}`;
  return key;
}

/** Spec 027, D13: where a person's feedback stands. */
export const FEEDBACK_STATE: Record<"open" | "proposed" | "closed", string> = {
  open: "ждёт предложения агента",
  proposed: "предложение — в «Решить»",
  closed: "решено",
};

/** The command for this computer (D5): `start.cmd` on Windows, `./start.sh` elsewhere. */
export function commandFor(commands: { windows: string; unix: string }, platform: string): string {
  return /windows|^win/i.test(platform) ? commands.windows : commands.unix;
}
